import { zodResolver } from "@hookform/resolvers/zod"
import { useQuery } from "@tanstack/react-query"
import { useCallback, useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import type { MediaChanges } from "@/components/media/mediaUploadSection"
import { LeaveConfirmationModal } from "@/components/modals/LeaveConfirmationModal"
import { type ProfilePhoto, useAuthStore } from "@/features/auth/store/authStore"
import { getCommunityById } from "@/features/communities/services/community.service"
import { ProfileIncompleteModal } from "@/features/profile/components/ProfileIncompleteModal"
import { isProfileFullyCompleted } from "@/features/profile/utils/profileCompletion"
import { useTourQueue } from "@/features/tour/hooks/useTourQueue"
import { redirectAfterRitualCreation } from "@/features/tour/utils/tour-redirect"
import { useUnsavedChangesGuard } from "@/hooks/useUnsavedChangesGuard"
import { useTranslation } from "@/language"
import { apiClient } from "@/lib/api-client"
import { getAssetUrl } from "@/utils/assets"
import type { CourseModule } from "../components/CourseModulesSection"
import { type Organizer as PreviewOrganizer, PreviewRitual } from "../components/PreviewRitual"
import { RitualForm } from "../components/RitualForm"
import { SuccessModal } from "../components/successModal"
import { useCreateRitual, useUpdateRitual } from "../hooks/useRitualMutations"
import { type RitualFormData, ritualSchema } from "../schemas/ritual.schema"
import type {
  CreateRitualRequest,
  MediaInput,
  RitualDuplicateOrganizer,
  UpdateRitualData,
} from "../services/ritual.service"
import { getRitualById, getRitualDuplicateData, publishRitual } from "../services/ritual.service"
import {
  RITUAL_TYPE_OPTIONS,
  RitualTypeEnum,
  type RitualTypeLabel,
  RitualVisibilityEnum,
} from "../types"
import type { Organizer } from "../utils/accordionValidation"
import { getDeselectedValuesForVisibility, getPrivacyConfig } from "../utils/privacyConfig"

interface SelectOption {
  value: string
  label: string
}

export function CreateRitualScreen() {
  const { t } = useTranslation("rituals")
  const [viewMode, setViewMode] = useState<"edit" | "preview">("edit")
  const user = useAuthStore(state => state.user)
  const [searchParams] = useSearchParams()
  const sourceId = searchParams.get("sourceId")
  const communityIdFromUrl = searchParams.get("communityId")

  const [showProfileIncompleteModal, setShowProfileIncompleteModal] = useState(false)
  const isProfileComplete = user ? isProfileFullyCompleted(user) : false

  useEffect(() => {
    if (user && !isProfileComplete) {
      setShowProfileIncompleteModal(true)
    }
  }, [user, isProfileComplete])

  const form = useForm<RitualFormData>({
    resolver: zodResolver(ritualSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      title: "",
      type: RitualTypeEnum.IN_PERSON,
      visibility: RitualVisibilityEnum.PUBLIC,
      dateTime: "",
      repeat: "none",
      repeatAt: "",
      repeatAtType: "DAYS",
      duration: "",
      address: "",
      meetingLink: "",
      steps: "",
      participationFee: "Free",
      price: "",
      feeDetails: "",
      minParticipantRequirement: false,
      minParticipants: "",
      registrationDeadline: "",
      description: "",
      whatToBring: "",
      whatYoullExperience: "",
      intention: "",
      websiteOrSocialMedia: "",
      tags: [],
      organizers: [],
      mediaFiles: {
        images: [],
        videos: [],
      },
      privacySettings: {
        visibility: ["everyone"],
        comment: ["owner", "organizers", "participants", "everyone"],
        edit: ["owner", "organizers"],
        join: ["everyone", "followers", "community_members"],
        invite: ["owner", "organizers", "participants"],
        seeParticipantList: ["owner", "organizers", "participants", "everyone"],
      },
    },
  })

  const { setValue, getValues, watch } = form

  // Warn before leaving with unsaved edits or an in-flight video upload.
  const [isVideoUploading, setIsVideoUploading] = useState(false)
  const unsavedGuard = useUnsavedChangesGuard(form.formState.isDirty || isVideoUploading)

  const { data: sourceData, isLoading: isLoadingSource } = useQuery({
    queryKey: ["ritual-duplicate", sourceId],
    queryFn: () => (sourceId ? getRitualDuplicateData(sourceId) : undefined),
    enabled: !!sourceId,
  })

  const { data: communityData } = useQuery({
    queryKey: ["community", communityIdFromUrl],
    queryFn: () => (communityIdFromUrl ? getCommunityById(communityIdFromUrl) : undefined),
    enabled: !!communityIdFromUrl,
  })

  const sourceInitializedRef = useRef(false)
  const communityInitializedRef = useRef(false)

  useEffect(() => {
    if (!sourceData || sourceInitializedRef.current) return
    sourceInitializedRef.current = true

    setValue("title", sourceData.title || "")
    setValue("type", sourceData.type as RitualTypeEnum)
    setValue("dateTime", new Date().toISOString())
    setValue("repeat", sourceData.repeat || "none")
    if (sourceData.repeatAt) setValue("repeatAt", sourceData.repeatAt)
    if (sourceData.repeatAtType) setValue("repeatAtType", sourceData.repeatAtType)
    setValue("duration", sourceData.duration || "")
    setValue("address", sourceData.streetAddress || "")
    setValue("visibility", sourceData.accessType as RitualVisibilityEnum)
    setValue("participationFee", sourceData.participationFee || "Free")
    if (sourceData.price !== null) setValue("price", sourceData.price.toString())
    if (sourceData.feeDetails) setValue("feeDetails", sourceData.feeDetails)
    setValue("description", sourceData.description || "")
    setValue("whatToBring", sourceData.whatToBring || "")
    setValue("whatYoullExperience", sourceData.experienceDetails || "")
    setValue("intention", sourceData.intention || "")
    if (sourceData.intentions && sourceData.intentions.length > 0) {
      setValue(
        "intentionIds",
        sourceData.intentions.map(i => i.id),
      )
      setSelectedIntentions(sourceData.intentions.map(i => ({ id: i.id, name: i.name })))
    }
    setValue("steps", sourceData.steps || "")
    setValue("meetingLink", sourceData.meetingLink || "")
    setValue("websiteOrSocialMedia", sourceData.webOrSocialMediaLink || "")
    setValue(
      "tags",
      (sourceData.tags || []).map(t => t.text),
    )
    setTags(sourceData.tags || [])
    setValue("minParticipantRequirement", sourceData.activateMinimumParticipantRequirement ?? false)
    if (sourceData.minParticipants)
      setValue("minParticipants", sourceData.minParticipants.toString())
    if (sourceData.registrationDeadline)
      setValue("registrationDeadline", sourceData.registrationDeadline)

    setPrivacySettings({
      comment: sourceData.whoCanComment || ["everyone"],
      edit: sourceData.whoCanEditRitual || ["organizers"],
      join: sourceData.whoCanJoin || ["everyone"],
      invite: sourceData.whoCanInvite || ["organizers"],
      seeParticipantList: sourceData.whoCanSeeParticipantList || ["everyone"],
    })

    if (sourceData.organizers && sourceData.organizers.length > 0) {
      const currentOrganizers = getValues("organizers")
      const existingIds = new Set(currentOrganizers.map(o => o.id))
      const newOrganizers: Organizer[] = sourceData.organizers
        .filter((o: RitualDuplicateOrganizer) => !existingIds.has(o.id))
        .map((o: RitualDuplicateOrganizer) => ({
          id: o.id,
          type: o.type as "user" | "community",
          name: o.name || "",
          identifier: o.username || null,
          avatar: o.profilePictureUrl || undefined,
          roleId: "host",
          roleName: t("createRitual.roleHost"),
          inviteNote: o.inviteNote || undefined,
        }))
      if (newOrganizers.length > 0) {
        setOrganizers(prev => [...prev, ...newOrganizers])
        setValue("organizers", [...currentOrganizers, ...newOrganizers])
      }
    }
  }, [sourceData, getValues, setValue, t])

  useEffect(() => {
    if (!sourceData?.courseModules?.length) return
    const mappedModules = sourceData.courseModules.map((m, idx: number) => ({
      id: crypto.randomUUID(),
      title: m.title || "",
      description: m.description || "",
      duration: m.duration || "",
      order: idx,
      isFree: false,
      uploadId: m.uploadId || undefined,
      video_url: m.video_url || undefined,
      videoPreview: m.video_url || undefined,
      videoFile: undefined,
    }))
    setCourseModules(mappedModules)
  }, [sourceData])

  const repeatOptions: SelectOption[] = [
    { value: "none", label: t("repeat.options.none") },
    { value: "daily", label: t("repeat.options.daily") },
    { value: "weekly", label: t("repeat.options.weekly") },
    { value: "monthly", label: t("repeat.options.monthly") },
    { value: "yearly", label: "Yearly" },
    { value: "custom", label: t("repeat.options.custom") },
  ]

  const participationFeeOptions: SelectOption[] = [
    { value: "Free", label: t("createRitual.options.participationFee.free") },
    { value: "Paid", label: t("createRitual.options.participationFee.paid") },
    { value: "Donation-based", label: t("createRitual.options.participationFee.donation") },
  ]

  const [tags, setTags] = useState<Array<{ id?: string; text: string }>>([])

  const [selectedIntentions, setSelectedIntentions] = useState<Array<{ id: string; name: string }>>(
    [],
  )

  const [privacySettings, setPrivacySettings] = useState(() => {
    const defaultConfig = getPrivacyConfig(RitualVisibilityEnum.PUBLIC)
    return {
      comment: defaultConfig.comment.defaultSelected,
      edit: defaultConfig.edit.defaultSelected,
      join: defaultConfig.join.defaultSelected,
      invite: defaultConfig.invite.defaultSelected,
      seeParticipantList: defaultConfig.seeParticipantList.defaultSelected,
    }
  })

  const prevVisibilityRef = useRef<RitualVisibilityEnum | null>(null)

  useEffect(() => {
    const currentVisibility = watch("visibility") as RitualVisibilityEnum
    // Skip if visibility is not set yet
    if (!currentVisibility) return

    // Initialize ref on first run
    if (prevVisibilityRef.current === null) {
      prevVisibilityRef.current = currentVisibility
      return
    }

    if (prevVisibilityRef.current !== currentVisibility) {
      prevVisibilityRef.current = currentVisibility
      const currentPrivacySettings = {
        comment: getValues("privacySettings.comment"),
        edit: getValues("privacySettings.edit"),
        join: getValues("privacySettings.join"),
        invite: getValues("privacySettings.invite"),
        seeParticipantList: getValues("privacySettings.seeParticipantList"),
      }
      const deselected = getDeselectedValuesForVisibility(currentVisibility, currentPrivacySettings)
      if (
        currentVisibility === RitualVisibilityEnum.PERSONAL ||
        currentVisibility === RitualVisibilityEnum.PRIVATE
      ) {
        setValue("minParticipantRequirement", false)
        setValue("minParticipants", "")
        setValue("registrationDeadline", "")
      }
      setPrivacySettings({
        comment: deselected.comment,
        edit: deselected.edit,
        join: deselected.join,
        invite: deselected.invite,
        seeParticipantList: deselected.seeParticipantList,
      })
      setValue("privacySettings.comment", deselected.comment)
      setValue("privacySettings.edit", deselected.edit)
      setValue("privacySettings.join", deselected.join)
      setValue("privacySettings.invite", deselected.invite)
      setValue("privacySettings.seeParticipantList", deselected.seeParticipantList)
    }
  }, [setValue, getValues, watch])

  const handleCommentChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, comment: options }))
      setValue("privacySettings.comment", options)
    },
    [setValue],
  )

  const handleEditChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, edit: options }))
      setValue("privacySettings.edit", options)
    },
    [setValue],
  )

  const handleJoinChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, join: options }))
      setValue("privacySettings.join", options)
    },
    [setValue],
  )

  const handleInviteChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, invite: options }))
      setValue("privacySettings.invite", options)
    },
    [setValue],
  )

  const handleSeeParticipantListChange = useCallback(
    (options: string[]) => {
      setPrivacySettings(prev => ({ ...prev, seeParticipantList: options }))
      setValue("privacySettings.seeParticipantList", options)
    },
    [setValue],
  )

  const handleCourseVideoAccessChange = useCallback(
    (value: "ALL" | "SEQUENTIAL") => {
      setCourseVideoAccess(value)
      setValue("courseVideoAccess", value)
    },
    [setValue],
  )

  const [organizers, setOrganizers] = useState<Organizer[]>(() => {
    if (user) {
      const coverPhoto = user.profile_photos?.find(
        (p: ProfilePhoto) => p.is_cover_image || p.isCover,
      )
      return [
        {
          id: user.id,
          type: "user",
          name: user.name || user.username || "You",
          identifier: user.username || null,
          avatar: coverPhoto?.filePath || user.profilePictureUrl || undefined,
          roleId: "host",
          roleName: t("createRitual.roleHost"),
        },
      ]
    }
    return []
  })
  const [isOrganizerModalOpen, setIsOrganizerModalOpen] = useState(false)

  useEffect(() => {
    if (!communityData || communityInitializedRef.current) return
    communityInitializedRef.current = true
    const existingIds = new Set(organizers.map(o => o.id))
    if (existingIds.has(communityData.id)) return
    const communityAsOrganizer: Organizer = {
      id: communityData.id,
      type: "community",
      name: communityData.name,
      identifier: communityData.handle,
      avatar: communityData.coverImages?.[0] || communityData.avatarUrl || undefined,
      roleId: "host",
      roleName: t("createRitual.roleHost"),
      inviteNote:
        "I would like to submit this ritual to your community for consideration. Kindly review and accept it.",
    }
    const updated = [...organizers, communityAsOrganizer]
    setOrganizers(updated)
    setValue("organizers", updated)
  }, [communityData, organizers, setValue, t])

  useEffect(() => {
    if (!user?.id) return
    apiClient
      .get<{ success: boolean; data: { followersCount: number } }>(
        `/api/profiles/${user.id}/statistics`,
      )
      .then(res => {
        const count = res.data.data.followersCount
        setOrganizers(prev =>
          prev.map(org => (org.id === user.id ? { ...org, dataCount: { followers: count } } : org)),
        )
      })
      .catch(() => {})
  }, [user?.id])

  const [mediaChanges, setMediaChanges] = useState<MediaChanges | null>(null)
  const [_hasCoverImage, setHasCoverImage] = useState(false)

  const handleMediaChange = useCallback((changes: MediaChanges) => {
    setMediaChanges(changes)
  }, [])

  const handleCoverImageChange = useCallback((hasCover: boolean) => {
    setHasCoverImage(hasCover)
  }, [])

  const [courseModules, setCourseModules] = useState<CourseModule[]>([])
  const [courseVideoAccess, setCourseVideoAccess] = useState<"ALL" | "SEQUENTIAL">("SEQUENTIAL")
  const [preservedValues, setPreservedValues] = useState({ meetingLink: "", steps: "" })

  const handleAddTag = (tag: string) => {
    const newTags = [...tags, { text: tag }]
    setTags(newTags)
    setValue(
      "tags",
      newTags.map(t => t.text),
    )
  }

  const handleRemoveTag = (tagToRemove: string) => {
    const updatedTags = tags.filter(tag => tag.text !== tagToRemove)
    setTags(updatedTags)
    setValue(
      "tags",
      updatedTags.map(t => t.text),
    )
  }

  const handleAddOrganizers = (
    newOrganizers: {
      id: string
      type: "user" | "community"
      name?: string | null
      identifier?: string | null
      profilePictureUrl?: string | null
      inviteNote?: string
      dataCount?: { followers?: number; members?: number }
    }[],
  ) => {
    const organizersWithType: Organizer[] = newOrganizers.map(org => ({
      id: org.id,
      type: org.type,
      name: org.name || "",
      identifier: org.identifier ?? null,
      avatar: org.profilePictureUrl || undefined,
      roleId: "host",
      roleName: t("createRitual.roleHost"),
      inviteNote: org.inviteNote,
      dataCount: org.dataCount,
    }))
    const updated = [...organizers, ...organizersWithType]
    setOrganizers(updated)
    setValue("organizers", updated)
  }

  const handleRemoveOrganizer = (organizerId: string) => {
    if (organizerId === user?.id) return
    const updated = organizers.filter(org => org.id !== organizerId)
    setOrganizers(updated)
    setValue("organizers", updated)
  }

  const handleAddModule = () => {
    const newModule: CourseModule = {
      id: `${Date.now()}-${Math.random()}`,
      title: "",
      description: "",
      duration: "",
      order: courseModules.length,
      isFree: true,
    }
    setCourseModules([...courseModules, newModule])
  }

  const handleRemoveModule = (moduleId: string) => {
    const updated = courseModules.filter(m => m.id !== moduleId)
    const reordered = updated.map((module, index) => ({ ...module, order: index }))
    setCourseModules(reordered)
  }

  const handleReorderModules = (modules: CourseModule[]) => {
    setCourseModules(modules)
  }

  const handleUpdateModule = (
    moduleId: string,
    fieldOrUpdates: keyof CourseModule | Partial<CourseModule>,
    value?: string | boolean | File,
  ) => {
    const updated = courseModules.map(module => {
      if (module.id === moduleId) {
        if (typeof fieldOrUpdates === "string") {
          return { ...module, [fieldOrUpdates]: value }
        }
        return { ...module, ...fieldOrUpdates }
      }
      return module
    })
    setCourseModules(updated)
  }

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [createdRitualId, setCreatedRitualId] = useState<string | null>(null)
  const [createdRitualSlug, setCreatedRitualSlug] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  // Two-step create: the Details "Save Draft & Next" mints the ritual as a draft
  // (this id), which unlocks the later sections and lets media upload live. Once
  // set, the create page edits/publishes this draft instead of creating again.
  const [draftRitualId, setDraftRitualId] = useState<string | null>(null)
  const _updateRitualMutation = useUpdateRitual(draftRitualId ?? "")
  // Server-side tags (with ids) of the minted draft, used to diff add/remove on
  // subsequent draft updates. Refreshed after mint and after each draft save.
  const [originalTagsWithIds, setOriginalTagsWithIds] = useState<
    Array<{ id: string; name: string }>
  >([])

  const _createRitualMutation = useCreateRitual()
  const tourQueue = useTourQueue()

  // After the draft is minted/updated, capture its persisted tags (with ids) so
  // post-mint add/remove edits can be diffed into the next update payload.
  const refreshTagBaseline = async (ritualId: string): Promise<void> => {
    try {
      const ritual = await getRitualById(ritualId)
      const withIds = (ritual.tags ?? [])
        .map(t => ({ id: t.id, name: t.tagText ?? t.name ?? "" }))
        .filter((t): t is { id: string; name: string } => !!t.id && !!t.name)
      setOriginalTagsWithIds(withIds)
    } catch {
      // Best-effort: if this fails, a tag removed this round simply won't diff.
    }
  }
  // RitualForm registers its native-video flushPending here so we can upload
  // any videos queued before the ritual existed, once we have the new id.
  const nativeVideoFlushRef = useRef<((newRitualId: string) => Promise<void>) | null>(null)

  const handleSaveDraft = async (isDraft: boolean = true): Promise<boolean> => {
    // Re-entrancy guard: a double-click (or preview + draft both firing) must
    // not create/update the ritual twice.
    if (isSubmitting) return false
    // A draft already exists -> update it (and publish on Create) instead of
    // creating a duplicate.
    if (draftRitualId) return handleUpdateDraft(isDraft)
    setIsSubmitting(true)
    setSubmitError(null)

    try {
      const durationMinutes = watch("duration") ? parseInt(watch("duration"), 10) : 30
      const mediaItems = mediaChanges?.mediaItems ?? []

      // Existing media (has uploadId - reuse without re-upload). Includes AUDIO:
      // the backend re-attaches the existing upload and carries over its title +
      // poster, so duplicating a ritual no longer drops its audio (#456).
      const existingMedia = mediaItems
        .filter((item): item is typeof item & { uploadId: string } => Boolean(item.uploadId))
        .map(item => ({ uploadId: item.uploadId, isCover: item.isCover }))

      const files: File[] = []
      const media: MediaInput[] = []
      const audioPosters: File[] = []
      const videoThumbnails: File[] = []
      let audioPosterIndex = 0
      let videoThumbnailIndex = 0

      mediaItems.forEach((item, index) => {
        if (!item.file) return
        files.push(item.file)
        const mediaEntry: MediaInput = { type: item.mediaType, sortOrder: index }
        if (item.mediaType === "IMAGE") mediaEntry.isCover = item.isCover
        if (item.mediaType === "VIDEO") {
          mediaEntry.title = item.videoTitle || undefined
          if (item.thumbnailFile) {
            mediaEntry.videoThumbnailIndex = videoThumbnailIndex
            videoThumbnails.push(item.thumbnailFile)
            videoThumbnailIndex++
          } else {
            mediaEntry.videoThumbnailIndex = null
          }
        }
        if (item.mediaType === "AUDIO") {
          mediaEntry.title = item.videoTitle || undefined
          if (item.thumbnailFile) {
            mediaEntry.posterIndex = audioPosterIndex
            audioPosters.push(item.thumbnailFile)
            audioPosterIndex++
          } else {
            mediaEntry.posterIndex = null
          }
        }
        media.push(mediaEntry)
      })

      const apiOrganizers =
        watch("visibility") === RitualVisibilityEnum.PERSONAL
          ? undefined
          : organizers.map(org => ({
              type: org.type as "user" | "community",
              id: org.id,
              inviteNote: org.inviteNote,
            }))

      const apiPrivacySettings =
        watch("visibility") === RitualVisibilityEnum.PERSONAL
          ? {
              whoCanSeeYourRitual: ["only_me"],
              whoCanComment: ["owner"],
              whoCanEditRitual: ["owner"],
              whoCanJoin: ["invite_req"],
              whoCanInvite: ["owner"],
              whoCanSeeParticipantList: ["owner"],
            }
          : {
              whoCanSeeYourRitual: watch("visibility")
                ? [watch("visibility").toLowerCase()]
                : ["everyone"],
              whoCanComment: privacySettings.comment,
              whoCanEditRitual: privacySettings.edit,
              whoCanJoin: privacySettings.join,
              whoCanInvite: privacySettings.invite,
              whoCanSeeParticipantList: privacySettings.seeParticipantList,
            }

      const getMeetingLink = () => {
        const formValue = watch("meetingLink")
        const preservedValue = preservedValues.meetingLink
        if (!formValue && preservedValue) return preservedValue || undefined
        return formValue || undefined
      }

      const getSteps = () => {
        const formValue = watch("steps")
        const preservedValue = preservedValues.steps
        if (!formValue && preservedValue) return preservedValue || undefined
        return formValue || undefined
      }

      const isPersonal = watch("visibility") === RitualVisibilityEnum.PERSONAL
      const isSelfGuided =
        watch("type") === RitualTypeEnum.SELF_GUIDED ||
        watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE

      const apiRequest: CreateRitualRequest = {
        participationFee: isPersonal
          ? "Free"
          : (watch("participationFee") as "Free" | "Paid" | "Donation-based"),
        title: watch("title") || "Untitled Ritual",
        type: watch("type") as RitualTypeEnum,
        date: new Date(watch("dateTime") || new Date().toISOString()).toISOString(),
        repeat: watch("repeat") as "none" | "daily" | "weekly" | "monthly" | "yearly" | "custom",
        repeatAt: watch("repeat") === "custom" ? watch("repeatAt") : undefined,
        repeatAtType: watch("repeat") === "custom" ? watch("repeatAtType") : undefined,
        duration: durationMinutes,
        city: watch("city") || undefined,
        state: watch("state") || undefined,
        country: watch("country") || undefined,
        lat: watch("lat"),
        long: watch("long"),
        meetingLink: getMeetingLink(),
        steps: getSteps(),
        description: watch("description") || undefined,
        intention: watch("intention") || undefined,
        intentionIds: watch("intentionIds") || [],
        accessType: watch("visibility") as RitualVisibilityEnum,
        communityId: communityIdFromUrl || undefined,
        tags: tags.map(t => t.text),
        existingMedia: existingMedia.length > 0 ? existingMedia : undefined,
        courseModules:
          watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE
            ? courseModules.map(m => ({
                title: m.title,
                description: m.description,
                duration: m.duration,
                order: m.order,
                isFree: m.isFree,
                uploadId: m.uploadId,
                videoFile: m.videoFile,
                thumbnailFile: m.thumbnailFile,
              }))
            : undefined,
        privacySettings: apiPrivacySettings,
        media,
        files,
        audioPosters: audioPosters.length > 0 ? audioPosters : undefined,
        videoThumbnails: videoThumbnails.length > 0 ? videoThumbnails : undefined,
        // The first POST always mints a DRAFT; publishing happens later via the
        // update + publish path once the user has added media/organizers.
        isDraft: true,
        courseVideoAccess:
          watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE ? courseVideoAccess : undefined,
        minParticipantRequirement:
          isPersonal || isSelfGuided ? undefined : watch("minParticipantRequirement"),
        minParticipants:
          isPersonal || isSelfGuided || !watch("minParticipantRequirement")
            ? undefined
            : watch("minParticipants")
              ? parseInt(watch("minParticipants") || "0", 10)
              : undefined,
        registrationDeadline:
          isPersonal || isSelfGuided || !watch("minParticipantRequirement")
            ? undefined
            : watch("registrationDeadline") || undefined,
      }

      if (!isPersonal) {
        apiRequest.streetAddress = watch("address") || undefined
        apiRequest.price = watch("price") ? parseFloat(watch("price") as string) : undefined
        apiRequest.feeDetails = watch("feeDetails") || undefined
        apiRequest.whatToBring = watch("whatToBring") || undefined
        apiRequest.experienceDetails = watch("whatYoullExperience") || undefined
        apiRequest.webOrSocialMediaLink = watch("websiteOrSocialMedia") || undefined
        apiRequest.organizers = apiOrganizers
      }

      if (
        !isPersonal &&
        apiRequest.webOrSocialMediaLink &&
        !apiRequest.webOrSocialMediaLink.match(/^https?:\/\//i)
      ) {
        apiRequest.webOrSocialMediaLink = `https://${apiRequest.webOrSocialMediaLink}`
      }

      const result = await _createRitualMutation.mutateAsync(apiRequest)

      // Draft minted. Stay in place and unlock the remaining sections; media now
      // uploads live to this id, and subsequent saves update/publish this ritual.
      setCreatedRitualId(result.id)
      setCreatedRitualSlug(result.slug ?? null)
      setDraftRitualId(result.id)
      await refreshTagBaseline(result.id)
      toast.success("Draft saved")
      return true
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        (err as { response?: { data?: { error?: string } } }).response?.data?.error ||
        (err instanceof Error ? err.message : "Failed to save draft")
      toast.error(errorMessage)
      setSubmitError(errorMessage)
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  // Once a draft exists, the bottom Save Draft / Create buttons UPDATE that draft
  // (Create also publishes). Media added in the now-unlocked section uploads as
  // mediaChanges.add (the draft has none yet); native videos already uploaded
  // live and only need reordering.
  const handleUpdateDraft = async (isDraft: boolean): Promise<boolean> => {
    if (!draftRitualId || isSubmitting) return false
    setIsSubmitting(true)
    setSubmitError(null)
    try {
      const durationMinutes = watch("duration") ? parseInt(watch("duration"), 10) : 30
      const isPersonal = watch("visibility") === RitualVisibilityEnum.PERSONAL
      const isSelfGuided =
        watch("type") === RitualTypeEnum.SELF_GUIDED ||
        watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE
      const shouldExcludeMin = isPersonal || isSelfGuided

      const updateData: UpdateRitualData = {
        title: watch("title"),
        type: watch("type"),
        accessType: watch("visibility") as RitualVisibilityEnum,
        date: new Date(watch("dateTime") || new Date().toISOString()).toISOString(),
        repeat: watch("repeat"),
        repeatAt: watch("repeat") === "custom" ? watch("repeatAt") : undefined,
        repeatAtType: watch("repeat") === "custom" ? watch("repeatAtType") : undefined,
        duration: durationMinutes,
        description: watch("description") || undefined,
        intention: watch("intention") || undefined,
        intentionIds: watch("intentionIds") || [],
        meetingLink:
          watch("type") === RitualTypeEnum.VIRTUAL_COURSE
            ? watch("meetingLink") || undefined
            : undefined,
        steps:
          watch("type") === RitualTypeEnum.SELF_GUIDED ? watch("steps") || undefined : undefined,
        activateMinimumParticipantRequirement: shouldExcludeMin
          ? false
          : watch("minParticipantRequirement"),
        minParticipants:
          shouldExcludeMin || !watch("minParticipantRequirement")
            ? undefined
            : watch("minParticipants")
              ? parseInt(watch("minParticipants") || "0", 10)
              : undefined,
        registrationDeadline:
          shouldExcludeMin || !watch("minParticipantRequirement")
            ? undefined
            : watch("registrationDeadline") || undefined,
      }

      // Diff tags against the draft's persisted tags so post-mint add/remove
      // edits are saved (add by text, remove by server id).
      const currentTagTexts = tags.map(t => t.text)
      const addedTags = currentTagTexts.filter(
        text => !originalTagsWithIds.some(ot => ot.name === text),
      )
      const removedTagIds = originalTagsWithIds
        .filter(ot => !currentTagTexts.includes(ot.name))
        .map(ot => ot.id)
      if (addedTags.length > 0 || removedTagIds.length > 0) {
        updateData.tags = {
          add: addedTags.length > 0 ? addedTags : undefined,
          remove: removedTagIds.length > 0 ? removedTagIds : undefined,
        }
      }

      if (isPersonal) {
        updateData.whoCanSeeYourRitual = ["only_me"]
        updateData.whoCanComment = ["owner"]
        updateData.whoCanEditRitual = ["owner"]
        updateData.whoCanJoin = ["invite_req"]
        updateData.whoCanInvite = ["owner"]
        updateData.whoCanSeeParticipantList = ["owner"]
      } else {
        updateData.streetAddress = watch("address") || undefined
        updateData.city = watch("city") || undefined
        updateData.state = watch("state") || undefined
        updateData.country = watch("country") || undefined
        updateData.lat = watch("lat")
        updateData.long = watch("long")
        updateData.participationFee = watch("participationFee")
        updateData.price = watch("price") ? parseFloat(watch("price") as string) : undefined
        updateData.feeDetails = watch("feeDetails") || undefined
        updateData.whatToBring = watch("whatToBring") || undefined
        updateData.experienceDetails = watch("whatYoullExperience") || undefined
        updateData.webOrSocialMediaLink = watch("websiteOrSocialMedia") || undefined
        if (watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE) {
          updateData.courseVideoAccess = courseVideoAccess
        }
        updateData.whoCanSeeYourRitual = watch("visibility")
          ? [watch("visibility").toLowerCase()]
          : ["everyone"]
        updateData.whoCanComment = privacySettings.comment
        updateData.whoCanEditRitual = privacySettings.edit
        updateData.whoCanJoin = privacySettings.join
        updateData.whoCanInvite = privacySettings.invite
        updateData.whoCanSeeParticipantList = privacySettings.seeParticipantList
        if (organizers.length > 0) {
          updateData.organizers = {
            add: organizers.map(o => ({
              type: o.type as "user" | "community",
              id: o.id,
              inviteNote: o.inviteNote,
            })),
          }
        }
      }

      // Media added after minting are all new (the draft has none) -> add them.
      const mediaItems = mediaChanges?.mediaItems ?? []
      const files: File[] = []
      const media: MediaInput[] = []
      mediaItems.forEach((item, index) => {
        if (item.uploadId || !item.file) return
        files.push(item.file)
        const entry: MediaInput = { type: item.mediaType, sortOrder: index }
        if (item.mediaType === "IMAGE") entry.isCover = item.isCover
        if (item.mediaType !== "IMAGE") entry.title = item.videoTitle || undefined
        media.push(entry)
      })
      const coverItem = mediaItems.find(item => item.isCover)
      const reorderedIds =
        mediaChanges?.orderedMediaIds && mediaChanges.orderedMediaIds.length > 0
          ? mediaChanges.orderedMediaIds
          : mediaItems
              .map(item => item.uploadId || item.id)
              .filter(id => id && !id.startsWith("ai-"))
      if (files.length > 0 || reorderedIds.length > 0 || coverItem) {
        updateData.mediaChanges = {
          add: {
            media: media.length > 0 ? media : undefined,
            files: files.length > 0 ? files : undefined,
          },
          reorder: reorderedIds.length > 0 ? reorderedIds : undefined,
          setCover:
            coverItem?.uploadId ||
            (coverItem?.id && !coverItem.id.startsWith("ai-") ? coverItem.id : undefined),
        }
      }

      if (watch("type") === RitualTypeEnum.SELF_GUIDED_COURSE) {
        updateData.courseModules = courseModules.map(m => ({
          id: m.id?.startsWith("new-") ? undefined : m.id,
          uploadId: m.uploadId,
          title: m.title,
          description: m.description,
          duration: m.duration,
          order: m.order,
          isFree: m.isFree,
          videoFile: m.videoFile,
          thumbnailFile: m.thumbnailFile,
        }))
      }

      await _updateRitualMutation.mutateAsync(updateData)

      if (isDraft) {
        // Refresh the baseline so newly-added tags pick up their server ids and
        // a later edit in the same session diffs against the saved state.
        await refreshTagBaseline(draftRitualId)
        toast.success("Draft saved")
        unsavedGuard.allowNavigation()
        window.location.href = "/my-rituality?tab=teaching&subTab=draft"
        return true
      }

      await publishRitual(draftRitualId)
      unsavedGuard.allowNavigation()
      await redirectAfterRitualCreation(draftRitualId, tourQueue)
      return true
    } catch (err) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ||
        (err instanceof Error ? err.message : "Failed to save ritual")
      toast.error(errorMessage)
      setSubmitError(errorMessage)
      return false
    } finally {
      setIsSubmitting(false)
    }
  }

  const handlePreview = () => {
    setViewMode("preview")
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const handleBackToEdit = () => {
    if (viewMode === "preview") {
      setViewMode("edit")
    }
  }

  const handleLeaveAnyway = () => {
    unsavedGuard.confirmLeave()
  }
  const handleKeepEditing = () => {
    unsavedGuard.cancelLeave()
  }

  // Leave protection (in-app LeaveConfirmationModal + native beforeunload for
  // tab close/refresh) is provided by useUnsavedChangesGuard above.

  const formatDate = (dateString: string) => {
    if (!dateString) return ""
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    } catch {
      return dateString
    }
  }

  const formatTime = (dateTimeString: string) => {
    if (!dateTimeString) return ""
    try {
      const date = new Date(dateTimeString)
      return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })
    } catch {
      return ""
    }
  }

  const blobUrlsRef = useRef<string[]>([])

  const getCoverImageUrl = () => {
    const items = mediaChanges?.mediaItems ?? []
    const images = items.filter(i => i.mediaType === "IMAGE")
    if (images.length === 0) return getAssetUrl("image.png")
    const coverItem = images.find(img => img.isCover) || images[0]
    if (coverItem.file) {
      const url = URL.createObjectURL(coverItem.file)
      blobUrlsRef.current.push(url)
      return url
    }
    return coverItem.src || getAssetUrl("image.png")
  }

  const getImageUrls = () => {
    const items = mediaChanges?.mediaItems ?? []
    const images = items.filter(i => i.mediaType === "IMAGE")
    if (images.length === 0) return []
    return images
      .map(img => {
        if (img.file) {
          const url = URL.createObjectURL(img.file)
          blobUrlsRef.current.push(url)
          return url
        }
        return img.src || null
      })
      .filter((url): url is string => url !== null)
  }

  const mapOrganizers = (): PreviewOrganizer[] => {
    return organizers.map(org => ({
      id: org.id,
      name: org.name,
      role: org.roleName,
      avatar: org.avatar,
      socialLinks: [],
      type: org.type,
      followers: org.type === "community" ? org.dataCount?.members : org.dataCount?.followers,
      isCreator: org.id === user?.id,
    }))
  }

  return (
    <>
      {isLoadingSource && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-brand-olive border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="text-sm text-muted-foreground">Loading ritual...</p>
          </div>
        </div>
      )}

      <RitualForm
        mode="create"
        form={form}
        draftExists={draftRitualId != null}
        onCreateDraft={() => handleSaveDraft(true)}
        ritualId={draftRitualId ?? ""}
        ritualTypeOptions={RITUAL_TYPE_OPTIONS}
        repeatOptions={repeatOptions}
        participationFeeOptions={participationFeeOptions}
        tags={tags}
        selectedIntentions={selectedIntentions}
        privacySettings={privacySettings}
        organizers={organizers}
        courseModules={courseModules}
        courseVideoAccess={courseVideoAccess}
        onCourseVideoAccessChange={handleCourseVideoAccessChange}
        preservedValues={preservedValues}
        mediaChanges={mediaChanges}
        initialMedia={mediaChanges?.mediaItems}
        user={user}
        onTagAdd={handleAddTag}
        onTagRemove={handleRemoveTag}
        onSetPreservedValues={setPreservedValues}
        onCommentChange={handleCommentChange}
        onEditChange={handleEditChange}
        onJoinChange={handleJoinChange}
        onInviteChange={handleInviteChange}
        onSeeParticipantListChange={handleSeeParticipantListChange}
        onMediaChange={handleMediaChange}
        onUploadingChange={setIsVideoUploading}
        onCoverImageChange={handleCoverImageChange}
        onAddModule={handleAddModule}
        onRemoveModule={handleRemoveModule}
        onReorderModules={handleReorderModules}
        onUpdateModule={handleUpdateModule}
        onAddOrganizers={handleAddOrganizers}
        onRemoveOrganizer={handleRemoveOrganizer}
        onAddIntention={intention => setSelectedIntentions(prev => [...prev, intention])}
        onRemoveIntention={id => setSelectedIntentions(prev => prev.filter(i => i.id !== id))}
        isSubmitting={isSubmitting}
        submitError={submitError}
        isOrganizerModalOpen={isOrganizerModalOpen}
        setIsOrganizerModalOpen={setIsOrganizerModalOpen}
        viewMode={viewMode}
        onPreview={handlePreview}
        onBackToEdit={handleBackToEdit}
        onSaveDraft={handleSaveDraft}
        registerNativeVideoFlush={flush => {
          nativeVideoFlushRef.current = flush
        }}
        renderPreview={() => (
          <PreviewRitual
            title={watch("title") || t("createRitual.fallback.untitledRitual")}
            description={watch("description") || ""}
            type={
              (RITUAL_TYPE_OPTIONS.find(opt => opt.value === watch("type"))?.label ||
                t("createRitual.fallback.inPerson")) as RitualTypeLabel
            }
            fee={
              watch("participationFee") === "Paid" && watch("price")
                ? `$${watch("price")}`
                : watch("participationFee") || t("createRitual.options.participationFee.free")
            }
            duration={watch("duration") || ""}
            date={formatDate(watch("dateTime") || "")}
            time={formatTime(watch("dateTime") || "")}
            repeat={
              repeatOptions.find(opt => opt.value === watch("repeat"))?.label ||
              t("repeat.options.none")
            }
            location={watch("address") || ""}
            meetingLink={watch("meetingLink") || undefined}
            websiteLink={watch("websiteOrSocialMedia") || undefined}
            whatYoullNeed={
              watch("whatToBring")
                ?.split("\n")
                .filter(item => item.trim()) || []
            }
            whatYoullExperience={
              watch("whatYoullExperience")
                ?.split("\n")
                .filter(item => item.trim()) || []
            }
            intention={watch("intention") || ""}
            intentions={selectedIntentions}
            steps={
              watch("steps")
                ?.split("\n")
                .filter(item => item.trim()) || []
            }
            organizers={mapOrganizers()}
            coverImage={getCoverImageUrl() || ""}
            images={getImageUrls()}
            tags={tags.map(t => t.text)}
            courseModules={
              watch("type") === "SELF_GUIDED_COURSE"
                ? courseModules.map(m => ({
                    id: m.id,
                    title: m.title,
                    description: m.description,
                    duration: m.duration,
                    order: m.order,
                    isFree: m.isFree,
                    videoUrl: m.videoPreview,
                  }))
                : undefined
            }
            onBackToEdit={handleBackToEdit}
            onCreateRitual={() => handleSaveDraft(false)}
            onSaveDraft={() => handleSaveDraft()}
            isSubmitting={isSubmitting}
          />
        )}
        renderModals={() => (
          <ProfileIncompleteModal
            isOpen={showProfileIncompleteModal}
            user={user}
            closeOnly={true}
            onClose={() => setShowProfileIncompleteModal(false)}
          />
        )}
      />

      <LeaveConfirmationModal
        isOpen={unsavedGuard.isBlocked}
        onKeepEditing={handleKeepEditing}
        onLeave={handleLeaveAnyway}
      />

      <SuccessModal
        isOpen={showSuccessModal}
        onViewRitual={async () => {
          setShowSuccessModal(false)
          if (createdRitualId) {
            await redirectAfterRitualCreation(createdRitualId, tourQueue)
          }
        }}
        onBackToRituality={() => {
          setShowSuccessModal(false)
          tourQueue.checkAndTriggerQueuedTour(
            "ritual_tour",
            createdRitualId ? { ritualId: createdRitualId } : undefined,
          )
          window.location.href = "/my-rituality"
        }}
        onShareRitual={() => {
          setShowSuccessModal(false)
          tourQueue.checkAndTriggerQueuedTour(
            "ritual_tour",
            createdRitualId ? { ritualId: createdRitualId } : undefined,
          )
          if (createdRitualId) window.location.href = `/rituals/${createdRitualId}?share=true`
        }}
        ritualId={createdRitualId || ""}
        ritualTitle={watch("title") || undefined}
        ritualDescription={watch("description") || ""}
        ritualImageUrl={getCoverImageUrl() || null}
        hideShare={watch("visibility") === RitualVisibilityEnum.PERSONAL}
        shareUrl={createdRitualId ? `/rituals/${createdRitualSlug || createdRitualId}` : ""}
        shareText={watch("title") ? `Check out this ritual: ${watch("title")}` : ""}
      />
    </>
  )
}
